// =============================================================================
// ESPOLIDAR WEB ADMIN - LAYOUT COMPONENT
// =============================================================================

import { Outlet, NavLink } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  LayoutDashboard,
  Cpu,
  Image,
  LogOut,
  Menu,
  X,
  Wifi,
  WifiOff,
  Building2,
  Users,
  Activity,
  BarChart3,
  FileSearch,
  Download,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { wsService } from '../services/websocket'

const viewerNavItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/events', label: 'Eventi', icon: Activity },
  { path: '/statistics', label: 'Statistiche', icon: BarChart3 },
  { path: '/analysis', label: 'Analisi', icon: FileSearch },
]

const adminNavItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/devices', label: 'Dispositivi', icon: Cpu },
  { path: '/media', label: 'Media', icon: Image },
  { path: '/events', label: 'Eventi', icon: Activity },
  { path: '/statistics', label: 'Statistiche', icon: BarChart3 },
  { path: '/analysis', label: 'Analisi', icon: FileSearch },
  { path: '/ota', label: 'Aggiornamenti OTA', icon: Download },
]

const superadminNavItems = [
  { path: '/resellers', label: 'Reseller', icon: Building2 },
  { path: '/users', label: 'Utenti', icon: Users },
]

export default function Layout() {
  const { user, logout } = useAuth()

  // Menu in base al ruolo
  const getNavItems = () => {
    if (user?.role === 'superadmin') {
      return [...adminNavItems, ...superadminNavItems]
    } else if (user?.role === 'admin') {
      return adminNavItems
    } else {
      // user / viewer
      return viewerNavItems
    }
  }

  const navItems = getNavItems()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [wsConnected, setWsConnected] = useState(false)

  useEffect(() => {
    const checkConnection = () => {
      setWsConnected(wsService.isConnected())
    }

    checkConnection()
    const interval = setInterval(checkConnection, 2000)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-30 h-full w-64 bg-gray-900 text-white
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <Cpu size={20} />
            </div>
            <span className="font-bold text-lg">IntelliExpo</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 hover:bg-gray-800 rounded"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              {wsConnected ? (
                <>
                  <Wifi size={16} className="text-green-500" />
                  <span>Connesso</span>
                </>
              ) : (
                <>
                  <WifiOff size={16} className="text-red-500" />
                  <span>Disconnesso</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{user?.username}</p>
              <p className="text-sm text-gray-400">{user?.role}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-10 h-16 bg-white shadow-sm flex items-center px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          >
            <Menu size={24} />
          </button>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
